import { Moment } from "moment";
import {
    RubberBandItem,
    SpiralItem,
    CalendarState,
    TemporaryCalendar,
    StepGuide,
    TextInformation,
} from "burde";
import { DayModuleItems, WeekModuleItems } from "svg/constants";
import { Dictionary } from "appz";

const LOCAL_STORAGE_RELOAD = "stp-auto-data";
const LOCAL_STORAGE_RELOAD_CALENDAR_A5 = "stp-auto-data-calendar-a5";
const LOCAL_STORAGE_RELOAD_CALENDAR_A6 = "stp-auto-data-calendar-a6";
const LOCAL_STORAGE_RELOAD_CALENDAR_B5 = "stp-auto-data-calendar-b5";
const LOCAL_STORAGE_RELOAD_NOTEBOOK_A5 = "stp-auto-data-notebook-a5";
const LOCAL_STORAGE_RELOAD_NOTEBOOK_A6 = "stp-auto-data-notebook-a6";
const LOCAL_STORAGE_RELOAD_NOTEBOOK_B5 = "stp-auto-data-notebook-b5";
const LOCAL_STORAGE_CHECKOUT = "stp-checkout-data";
const LOCAL_STORAGE_CONFIGURATION = "stp-configuration-data";
const COOKIE_USER_JWT = "JWT";
const LOCAL_STORAGE_UPLOADED_COVER_IMAGES = "stp-cover-images";
const LOCAL_STORAGE_UPLOADED_OWNER_PAGE_IMAGES = "stp-owner-page-images";
const COOKIE_USER_EXPIRE_DAYS = 365;

const MaxTemporaryCalendarStorageHours = 168;

type NavigationConfig = {
    type: string;
    options: Array<NavigationOption>;
};

type NavigationOption = {
    route: string;
    components: Array<NavigationComponent>;
};

type NavConfig = {
    type: string;
    components: Array<NavigationComponent>;
};

type NavigationComponent = {
    hideInMainNavigation?: boolean | null;
    group?: string | null;
    resourceKey: string;
    name: string;
    step: string;
    route: string;
    values?: Array<NavigationValue>;
    showAsLink?: boolean;
};

type NavigationValue = {
    resourceKey: string;
    valuePath: string;
    type: string;
    sourceRoute?: string;
    showIfEmpty?: boolean;
    mapper?: Array<{ key: string; value: string }>;
};

type MainNavigationItem = {
    group: string;
    components: Array<NavigationComponent>;
};

type LocalStorageReloadItem = {
    timestamp: Moment;
    calendar: CalendarState;
    activeNavigationComponent: NavigationComponent;
};

type LocalStorageTemporaryCalendar = {
    timestamp: Moment;
    temporaryCalendar: TemporaryCalendar;
};

type LocalStorageCheckoutItem = {
    timestamp: Moment;
    calendar: CalendarState;
};

type LayoutApiResponse = {
    defaultLayout: number;
    layoutResources: Array<LayoutResource>;
};

type LayoutResource = {
    resourceGroupTitle: string;
    layouts: Array<SvgCalendarLayout>;
};

type LayoutDefaultValues = {
    borderColor: string;
    borderImage: number | null;
};

type SvgCalendarLayout = {
    id: number;
    title: string;
    description: string;
    endpoints: LayoutEndpoints;
    fonts: Array<string>;
    lines: Array<string>;
    maxDayModules: number;
    maxWeekModules: number;
    showTimestamps: boolean;
    accentColor: string;
    defaultValues: LayoutDefaultValues;
};

type LayoutEndpoints = {
    thumbnail: string;
    leftLayout?: LayoutPage;
    rightLayout?: LayoutPage;
};

type LayoutFont = {
    title: string;
    thumbnail: string;
    url: string;
    "font-family": string;
    format: string;
};

type LayoutPage = {
    svgData: string;
    numberOfDays: number;
};

type ModuleResource = {
    resourceGroupTitle: string;
    modules: Array<ModuleOption>;
};

type ModuleOption = {
    id: number;
    title: string;
    accentColor: string | null;
    endpoints: ModuleEndpoints;
};

interface DayModuleResource extends ModuleResource {}

interface DayModuleOption extends ModuleOption {}

interface WeekModuleResource extends ModuleResource {}

interface WeekModuleOption extends ModuleOption {}

type ModuleEndpoints = {
    thumbnail?: string;
    svgData?: string;
};

interface DayModuleElement extends ModuleElement {}

interface WeekModuleElement extends ModuleElement {}

type ModuleElement = {
    slotId: number;
    id: number;
    title: string;
    accentColor: string | null;
    endpoints: ModuleEndpoints;
};

const LightBoxTheme = {
    arrow: {
        backgroundColor: "#d6d6d6",
        fill: "black",
        borderRadius: "25px",
        opacity: "0.8",
        transition: "opacity 200ms",
        ":hover": {
            opacity: 1,
        },
    },
    arrow__size__medium: {
        borderRadius: 40,
        height: 40,
        marginTop: -20,

        "@media (min-width: 768px)": {
            height: 70,
            padding: 15,
        },
    },
};

const removeEmojis = (input: string) => {
    var emojiRegex = /\uD83C\uDFF4(?:\uDB40\uDC67\uDB40\uDC62(?:\uDB40\uDC65\uDB40\uDC6E\uDB40\uDC67|\uDB40\uDC77\uDB40\uDC6C\uDB40\uDC73|\uDB40\uDC73\uDB40\uDC63\uDB40\uDC74)\uDB40\uDC7F|\u200D\u2620\uFE0F)|\uD83D\uDC69\u200D\uD83D\uDC69\u200D(?:\uD83D\uDC66\u200D\uD83D\uDC66|\uD83D\uDC67\u200D(?:\uD83D[\uDC66\uDC67]))|\uD83D\uDC68(?:\u200D(?:\u2764\uFE0F\u200D(?:\uD83D\uDC8B\u200D)?\uD83D\uDC68|(?:\uD83D[\uDC68\uDC69])\u200D(?:\uD83D\uDC66\u200D\uD83D\uDC66|\uD83D\uDC67\u200D(?:\uD83D[\uDC66\uDC67]))|\uD83D\uDC66\u200D\uD83D\uDC66|\uD83D\uDC67\u200D(?:\uD83D[\uDC66\uDC67])|\uD83C[\uDF3E\uDF73\uDF93\uDFA4\uDFA8\uDFEB\uDFED]|\uD83D[\uDCBB\uDCBC\uDD27\uDD2C\uDE80\uDE92]|\uD83E[\uDDB0-\uDDB3])|(?:\uD83C[\uDFFB-\uDFFF])\u200D(?:\uD83C[\uDF3E\uDF73\uDF93\uDFA4\uDFA8\uDFEB\uDFED]|\uD83D[\uDCBB\uDCBC\uDD27\uDD2C\uDE80\uDE92]|\uD83E[\uDDB0-\uDDB3]))|\uD83D\uDC69\u200D(?:\u2764\uFE0F\u200D(?:\uD83D\uDC8B\u200D(?:\uD83D[\uDC68\uDC69])|\uD83D[\uDC68\uDC69])|\uD83C[\uDF3E\uDF73\uDF93\uDFA4\uDFA8\uDFEB\uDFED]|\uD83D[\uDCBB\uDCBC\uDD27\uDD2C\uDE80\uDE92]|\uD83E[\uDDB0-\uDDB3])|\uD83D\uDC69\u200D\uD83D\uDC66\u200D\uD83D\uDC66|(?:\uD83D\uDC41\uFE0F\u200D\uD83D\uDDE8|\uD83D\uDC69(?:\uD83C[\uDFFB-\uDFFF])\u200D[\u2695\u2696\u2708]|\uD83D\uDC68(?:(?:\uD83C[\uDFFB-\uDFFF])\u200D[\u2695\u2696\u2708]|\u200D[\u2695\u2696\u2708])|(?:(?:\u26F9|\uD83C[\uDFCB\uDFCC]|\uD83D\uDD75)\uFE0F|\uD83D\uDC6F|\uD83E[\uDD3C\uDDDE\uDDDF])\u200D[\u2640\u2642]|(?:\u26F9|\uD83C[\uDFCB\uDFCC]|\uD83D\uDD75)(?:\uD83C[\uDFFB-\uDFFF])\u200D[\u2640\u2642]|(?:\uD83C[\uDFC3\uDFC4\uDFCA]|\uD83D[\uDC6E\uDC71\uDC73\uDC77\uDC81\uDC82\uDC86\uDC87\uDE45-\uDE47\uDE4B\uDE4D\uDE4E\uDEA3\uDEB4-\uDEB6]|\uD83E[\uDD26\uDD37-\uDD39\uDD3D\uDD3E\uDDB8\uDDB9\uDDD6-\uDDDD])(?:(?:\uD83C[\uDFFB-\uDFFF])\u200D[\u2640\u2642]|\u200D[\u2640\u2642])|\uD83D\uDC69\u200D[\u2695\u2696\u2708])\uFE0F|\uD83D\uDC69\u200D\uD83D\uDC67\u200D(?:\uD83D[\uDC66\uDC67])|\uD83D\uDC69\u200D\uD83D\uDC69\u200D(?:\uD83D[\uDC66\uDC67])|\uD83D\uDC68(?:\u200D(?:(?:\uD83D[\uDC68\uDC69])\u200D(?:\uD83D[\uDC66\uDC67])|\uD83D[\uDC66\uDC67])|\uD83C[\uDFFB-\uDFFF])|\uD83C\uDFF3\uFE0F\u200D\uD83C\uDF08|\uD83D\uDC69\u200D\uD83D\uDC67|\uD83D\uDC69(?:\uD83C[\uDFFB-\uDFFF])\u200D(?:\uD83C[\uDF3E\uDF73\uDF93\uDFA4\uDFA8\uDFEB\uDFED]|\uD83D[\uDCBB\uDCBC\uDD27\uDD2C\uDE80\uDE92]|\uD83E[\uDDB0-\uDDB3])|\uD83D\uDC69\u200D\uD83D\uDC66|\uD83C\uDDF6\uD83C\uDDE6|\uD83C\uDDFD\uD83C\uDDF0|\uD83C\uDDF4\uD83C\uDDF2|\uD83D\uDC69(?:\uD83C[\uDFFB-\uDFFF])|\uD83C\uDDED(?:\uD83C[\uDDF0\uDDF2\uDDF3\uDDF7\uDDF9\uDDFA])|\uD83C\uDDEC(?:\uD83C[\uDDE6\uDDE7\uDDE9-\uDDEE\uDDF1-\uDDF3\uDDF5-\uDDFA\uDDFC\uDDFE])|\uD83C\uDDEA(?:\uD83C[\uDDE6\uDDE8\uDDEA\uDDEC\uDDED\uDDF7-\uDDFA])|\uD83C\uDDE8(?:\uD83C[\uDDE6\uDDE8\uDDE9\uDDEB-\uDDEE\uDDF0-\uDDF5\uDDF7\uDDFA-\uDDFF])|\uD83C\uDDF2(?:\uD83C[\uDDE6\uDDE8-\uDDED\uDDF0-\uDDFF])|\uD83C\uDDF3(?:\uD83C[\uDDE6\uDDE8\uDDEA-\uDDEC\uDDEE\uDDF1\uDDF4\uDDF5\uDDF7\uDDFA\uDDFF])|\uD83C\uDDFC(?:\uD83C[\uDDEB\uDDF8])|\uD83C\uDDFA(?:\uD83C[\uDDE6\uDDEC\uDDF2\uDDF3\uDDF8\uDDFE\uDDFF])|\uD83C\uDDF0(?:\uD83C[\uDDEA\uDDEC-\uDDEE\uDDF2\uDDF3\uDDF5\uDDF7\uDDFC\uDDFE\uDDFF])|\uD83C\uDDEF(?:\uD83C[\uDDEA\uDDF2\uDDF4\uDDF5])|\uD83C\uDDF8(?:\uD83C[\uDDE6-\uDDEA\uDDEC-\uDDF4\uDDF7-\uDDF9\uDDFB\uDDFD-\uDDFF])|\uD83C\uDDEE(?:\uD83C[\uDDE8-\uDDEA\uDDF1-\uDDF4\uDDF6-\uDDF9])|\uD83C\uDDFF(?:\uD83C[\uDDE6\uDDF2\uDDFC])|\uD83C\uDDEB(?:\uD83C[\uDDEE-\uDDF0\uDDF2\uDDF4\uDDF7])|\uD83C\uDDF5(?:\uD83C[\uDDE6\uDDEA-\uDDED\uDDF0-\uDDF3\uDDF7-\uDDF9\uDDFC\uDDFE])|\uD83C\uDDE9(?:\uD83C[\uDDEA\uDDEC\uDDEF\uDDF0\uDDF2\uDDF4\uDDFF])|\uD83C\uDDF9(?:\uD83C[\uDDE6\uDDE8\uDDE9\uDDEB-\uDDED\uDDEF-\uDDF4\uDDF7\uDDF9\uDDFB\uDDFC\uDDFF])|\uD83C\uDDE7(?:\uD83C[\uDDE6\uDDE7\uDDE9-\uDDEF\uDDF1-\uDDF4\uDDF6-\uDDF9\uDDFB\uDDFC\uDDFE\uDDFF])|[#\*0-9]\uFE0F\u20E3|\uD83C\uDDF1(?:\uD83C[\uDDE6-\uDDE8\uDDEE\uDDF0\uDDF7-\uDDFB\uDDFE])|\uD83C\uDDE6(?:\uD83C[\uDDE8-\uDDEC\uDDEE\uDDF1\uDDF2\uDDF4\uDDF6-\uDDFA\uDDFC\uDDFD\uDDFF])|\uD83C\uDDF7(?:\uD83C[\uDDEA\uDDF4\uDDF8\uDDFA\uDDFC])|\uD83C\uDDFB(?:\uD83C[\uDDE6\uDDE8\uDDEA\uDDEC\uDDEE\uDDF3\uDDFA])|\uD83C\uDDFE(?:\uD83C[\uDDEA\uDDF9])|(?:\uD83C[\uDFC3\uDFC4\uDFCA]|\uD83D[\uDC6E\uDC71\uDC73\uDC77\uDC81\uDC82\uDC86\uDC87\uDE45-\uDE47\uDE4B\uDE4D\uDE4E\uDEA3\uDEB4-\uDEB6]|\uD83E[\uDD26\uDD37-\uDD39\uDD3D\uDD3E\uDDB8\uDDB9\uDDD6-\uDDDD])(?:\uD83C[\uDFFB-\uDFFF])|(?:\u26F9|\uD83C[\uDFCB\uDFCC]|\uD83D\uDD75)(?:\uD83C[\uDFFB-\uDFFF])|(?:[\u261D\u270A-\u270D]|\uD83C[\uDF85\uDFC2\uDFC7]|\uD83D[\uDC42\uDC43\uDC46-\uDC50\uDC66\uDC67\uDC70\uDC72\uDC74-\uDC76\uDC78\uDC7C\uDC83\uDC85\uDCAA\uDD74\uDD7A\uDD90\uDD95\uDD96\uDE4C\uDE4F\uDEC0\uDECC]|\uD83E[\uDD18-\uDD1C\uDD1E\uDD1F\uDD30-\uDD36\uDDB5\uDDB6\uDDD1-\uDDD5])(?:\uD83C[\uDFFB-\uDFFF])|(?:[\u231A\u231B\u23E9-\u23EC\u23F0\u23F3\u25FD\u25FE\u2614\u2615\u2648-\u2653\u267F\u2693\u26A1\u26AA\u26AB\u26BD\u26BE\u26C4\u26C5\u26CE\u26D4\u26EA\u26F2\u26F3\u26F5\u26FA\u26FD\u2705\u270A\u270B\u2728\u274C\u274E\u2753-\u2755\u2757\u2795-\u2797\u27B0\u27BF\u2B1B\u2B1C\u2B50\u2B55]|\uD83C[\uDC04\uDCCF\uDD8E\uDD91-\uDD9A\uDDE6-\uDDFF\uDE01\uDE1A\uDE2F\uDE32-\uDE36\uDE38-\uDE3A\uDE50\uDE51\uDF00-\uDF20\uDF2D-\uDF35\uDF37-\uDF7C\uDF7E-\uDF93\uDFA0-\uDFCA\uDFCF-\uDFD3\uDFE0-\uDFF0\uDFF4\uDFF8-\uDFFF]|\uD83D[\uDC00-\uDC3E\uDC40\uDC42-\uDCFC\uDCFF-\uDD3D\uDD4B-\uDD4E\uDD50-\uDD67\uDD7A\uDD95\uDD96\uDDA4\uDDFB-\uDE4F\uDE80-\uDEC5\uDECC\uDED0-\uDED2\uDEEB\uDEEC\uDEF4-\uDEF9]|\uD83E[\uDD10-\uDD3A\uDD3C-\uDD3E\uDD40-\uDD45\uDD47-\uDD70\uDD73-\uDD76\uDD7A\uDD7C-\uDDA2\uDDB0-\uDDB9\uDDC0-\uDDC2\uDDD0-\uDDFF])|(?:[#\*0-9\xA9\xAE\u203C\u2049\u2122\u2139\u2194-\u2199\u21A9\u21AA\u231A\u231B\u2328\u23CF\u23E9-\u23F3\u23F8-\u23FA\u24C2\u25AA\u25AB\u25B6\u25C0\u25FB-\u25FE\u2600-\u2604\u260E\u2611\u2614\u2615\u2618\u261D\u2620\u2622\u2623\u2626\u262A\u262E\u262F\u2638-\u263A\u2640\u2642\u2648-\u2653\u265F\u2660\u2663\u2665\u2666\u2668\u267B\u267E\u267F\u2692-\u2697\u2699\u269B\u269C\u26A0\u26A1\u26AA\u26AB\u26B0\u26B1\u26BD\u26BE\u26C4\u26C5\u26C8\u26CE\u26CF\u26D1\u26D3\u26D4\u26E9\u26EA\u26F0-\u26F5\u26F7-\u26FA\u26FD\u2702\u2705\u2708-\u270D\u270F\u2712\u2714\u2716\u271D\u2721\u2728\u2733\u2734\u2744\u2747\u274C\u274E\u2753-\u2755\u2757\u2763\u2764\u2795-\u2797\u27A1\u27B0\u27BF\u2934\u2935\u2B05-\u2B07\u2B1B\u2B1C\u2B50\u2B55\u3030\u303D\u3297\u3299]|\uD83C[\uDC04\uDCCF\uDD70\uDD71\uDD7E\uDD7F\uDD8E\uDD91-\uDD9A\uDDE6-\uDDFF\uDE01\uDE02\uDE1A\uDE2F\uDE32-\uDE3A\uDE50\uDE51\uDF00-\uDF21\uDF24-\uDF93\uDF96\uDF97\uDF99-\uDF9B\uDF9E-\uDFF0\uDFF3-\uDFF5\uDFF7-\uDFFF]|\uD83D[\uDC00-\uDCFD\uDCFF-\uDD3D\uDD49-\uDD4E\uDD50-\uDD67\uDD6F\uDD70\uDD73-\uDD7A\uDD87\uDD8A-\uDD8D\uDD90\uDD95\uDD96\uDDA4\uDDA5\uDDA8\uDDB1\uDDB2\uDDBC\uDDC2-\uDDC4\uDDD1-\uDDD3\uDDDC-\uDDDE\uDDE1\uDDE3\uDDE8\uDDEF\uDDF3\uDDFA-\uDE4F\uDE80-\uDEC5\uDECB-\uDED2\uDEE0-\uDEE5\uDEE9\uDEEB\uDEEC\uDEF0\uDEF3-\uDEF9]|\uD83E[\uDD10-\uDD3A\uDD3C-\uDD3E\uDD40-\uDD45\uDD47-\uDD70\uDD73-\uDD76\uDD7A\uDD7C-\uDDA2\uDDB0-\uDDB9\uDDC0-\uDDC2\uDDD0-\uDDFF])\uFE0F|(?:[\u261D\u26F9\u270A-\u270D]|\uD83C[\uDF85\uDFC2-\uDFC4\uDFC7\uDFCA-\uDFCC]|\uD83D[\uDC42\uDC43\uDC46-\uDC50\uDC66-\uDC69\uDC6E\uDC70-\uDC78\uDC7C\uDC81-\uDC83\uDC85-\uDC87\uDCAA\uDD74\uDD75\uDD7A\uDD90\uDD95\uDD96\uDE45-\uDE47\uDE4B-\uDE4F\uDEA3\uDEB4-\uDEB6\uDEC0\uDECC]|\uD83E[\uDD18-\uDD1C\uDD1E\uDD1F\uDD26\uDD30-\uDD39\uDD3D\uDD3E\uDDB5\uDDB6\uDDB8\uDDB9\uDDD1-\uDDDD])/g;
    return input.replace(emojiRegex, "");
};

const CalendarSteps = {
    cover: "cover",
    ownerPage: "owner-page",
    insert: "insert",
    endPages: "end-pages",
    summary: "summary",
    extraPages: "extra-pages",
    monthDivider: "month-divider",
};

const NavigationComponents: Array<NavigationComponent> = [
    {
        group: "NavControls_GroupCoverPage",
        resourceKey: "NavControls_CoverPage",
        name: "coverPage",
        step: CalendarSteps.cover,
        route: "/{calendarType}/{calendarSize}/" + CalendarSteps.cover,
        values: [
            {
                resourceKey: "NavControls_ColorAndImage",
                type: "color",
                valuePath: "cover.color",
            },
            {
                resourceKey: "NavControls_ColorAndImage",
                type: "image",
                valuePath: "cover.image",
                sourceRoute: "images/cover/",
            },
            {
                resourceKey: "NavControls_ColorAndImage",
                type: "image",
                valuePath: "cover.customImage.url",
            },
        ],
    },
    {
        group: "NavControls_GroupCoverPage",
        resourceKey: "NavControls_CoverText",
        name: "coverText",
        step: CalendarSteps.cover,
        route: "/{calendarType}/{calendarSize}/" + CalendarSteps.cover,
        values: [
            {
                resourceKey: "NavControls_SelectedText",
                type: "coverText",
                valuePath: "cover.texts",
            },
        ],
    },
    {
        group: "NavControls_GroupBackpage",
        resourceKey: "NavControls_Backpage",
        name: "backpage",
        step: CalendarSteps.cover,
        route: "/{calendarType}/{calendarSize}/" + CalendarSteps.cover,
        values: [
            {
                resourceKey: "NavControls_ColorAndImage",
                type: "color",
                valuePath: "cover.backColor",
            },
            {
                resourceKey: "NavControls_ColorAndImage",
                type: "image",
                valuePath: "cover.backImage",
                sourceRoute: "images/cover/",
            },
            {
                resourceKey: "NavControls_ColorAndImage",
                type: "image",
                valuePath: "cover.backCustomImage.url",
            },
        ],
    },
    {
        group: "NavControls_GroupCoverPage",
        resourceKey: "NavControls_OwnerPage",
        name: "ownerPage",
        step: CalendarSteps.ownerPage,
        route: "/{calendarType}/{calendarSize}/" + CalendarSteps.ownerPage,
        values: [],
    },
    {
        group: "NavControls_GroupContent",
        resourceKey: "NavControls_Layout",
        name: "layout",
        step: CalendarSteps.insert,
        route: "/{calendarType}/{calendarSize}/" + CalendarSteps.insert,
        values: [
            {
                resourceKey: "NavControls_Layout",
                type: "text",
                valuePath: "calendarLayout",
                mapper: [
                    {
                        key: "Standing",
                        value: "NavControls_Layout_Standing",
                    },
                    {
                        key: "Laying",
                        value: "NavControls_Layout_Portrait",
                    },
                    {
                        key: "Layingleft",
                        value: "NavControls_Layout_PortraitLeft",
                    },
                ],
            },
        ],
    },
    {
        group: "NavControls_GroupContent",
        resourceKey: "NavControls_Frame",
        name: "frame",
        step: CalendarSteps.insert,
        route: "/{calendarType}/{calendarSize}/" + CalendarSteps.insert,
        values: [
            {
                resourceKey: "NavControls_Frame",
                type: "color",
                valuePath: "frameColor",
            },
            {
                resourceKey: "NavControls_Frame",
                type: "frameImage",
                valuePath: "frame",
                sourceRoute: "images/frames/",
            },
        ],
    },
    {
        group: "NavControls_GroupContent",
        resourceKey: "NavControls_DayModules",
        name: "dayModules",
        step: CalendarSteps.insert,
        route: "/{calendarType}/{calendarSize}/" + CalendarSteps.insert,
        values: [
            {
                resourceKey: "NavControls_DayModules",
                type: "modules",
                valuePath: "dayModules",
                showIfEmpty: true,
                mapper: DayModuleItems.map((item) => {
                    return {
                        key: item.value,
                        value: item.icon as string,
                    };
                }),
            },
        ],
    },
    {
        group: "NavControls_GroupContent",
        resourceKey: "NavControls_WeekModules",
        name: "weekModules",
        step: CalendarSteps.insert,
        route: "/{calendarType}/{calendarSize}/" + CalendarSteps.insert,
        values: [
            {
                resourceKey: "NavControls_WeekModules",
                type: "modules",
                valuePath: "weekModules",
                showIfEmpty: true,
                mapper: WeekModuleItems.map((item) => {
                    return {
                        key: item.value,
                        value: item.icon as string,
                    };
                }),
            },
        ],
    },
    {
        group: "NavControls_GroupContent",
        resourceKey: "NavControls_PersonalDates",
        name: "personalDates",
        step: CalendarSteps.insert,
        route: "/{calendarType}/{calendarSize}/" + CalendarSteps.insert,
        values: [
            {
                resourceKey: "NavControls_PersonalDates",
                type: "personalDates",
                valuePath: "personalDateGroups",
            },
        ],
    },
    {
        group: "NavControls_GroupOther",
        resourceKey: "NavControls_MonthDivider",
        name: "monthDivider",
        step: CalendarSteps.monthDivider,
        route: "/{calendarType}/{calendarSize}/" + CalendarSteps.monthDivider,
        values: [
            {
                resourceKey: "NavControls_MonthDivider",
                type: "monthDivider",
                valuePath: "monthDivider",
                showIfEmpty: true,
            },
        ],
    },
    {
        group: "NavControls_GroupOther",
        resourceKey: "NavControls_ExtraPages",
        name: "extraPages",
        step: CalendarSteps.extraPages,
        route: "/{calendarType}/{calendarSize}/" + CalendarSteps.extraPages,
        values: [
            {
                resourceKey: "NavControls_ExtraPages",
                type: "extraPages",
                valuePath: "extraPages",
                showIfEmpty: true,
            },
        ],
    },
    {
        group: "NavControls_GroupOther",
        resourceKey: "NavControls_Summary",
        name: "summary",
        step: CalendarSteps.summary,
        route: "/{calendarType}/{calendarSize}/" + CalendarSteps.summary,
        showAsLink: true,
    },
];

const CalendarStepGuide: StepGuide[] = [
    {
        type: "A5",
        steps: [
            "coverPage",
            "coverText",
            "backpage",
            "ownerPage",
            "layout",
            "frame",
            "dayModules",
            "weekModules",
            "personalDates",
            "monthDivider",
            "extraPages",
            "summary",
        ],
    },
    {
        type: "A6",
        steps: [
            "coverPage",
            "coverText",
            "backpage",
            "ownerPage",
            "layout",
            "frame",
            "dayModules",
            "weekModules",
            "personalDates",
            "monthDivider",
            "extraPages",
            "summary",
        ],
    },
    {
        type: "B5",
        steps: [
            "coverPage",
            "coverText",
            "backpage",
            "ownerPage",
            "layout",
            "frame",
            "dayModules",
            "weekModules",
            "personalDates",
            "monthDivider",
            "extraPages",
            "summary",
        ],
    },
];

const FastTrackStepGuide: StepGuide[] = [
    {
        type: "A5",
        steps: ["coverPage", "coverText", "backpage", "summary"],
    },
    {
        type: "A6",
        steps: ["coverPage", "coverText", "backpage", "summary"],
    },
    {
        type: "B5",
        steps: ["coverPage", "coverText", "backpage", "summary"],
    },
];

const NotebookStepGuide: StepGuide[] = [
    {
        type: "A5",
        steps: [
            "coverPage",
            "coverText",
            "backpage",
            "ownerPage",
            "layout",
            "frame",
            "dayModules",
            "weekModules",
            "extraPages",
            "summary",
        ],
    },
    {
        type: "A6",
        steps: [
            "coverPage",
            "coverText",
            "backpage",
            "ownerPage",
            "layout",
            "frame",
            "dayModules",
            "weekModules",
            "extraPages",
            "summary",
        ],
    },
    {
        type: "B5",
        steps: [
            "coverPage",
            "coverText",
            "backpage",
            "ownerPage",
            "layout",
            "frame",
            "dayModules",
            "weekModules",
            "extraPages",
            "summary",
        ],
    },
];

// Use 1904 as the recurring year since that's a leap year
const RecurringYear = 1904;

const DefaultSize: string = "A5";

const DefaultCulture: string = "sv-SE";

const DefaultCurrency: string = "SEK";

const DefaultType: string = "calendar";

const NotebookArticleNames: Dictionary<string> = {
    A5: "NOTEBOOK_A5_001",
    A6: "NOTEBOOK_A6_001",
};

const CalendarArticleNames: Dictionary<string> = {
    A5: "CALENDAR_A5_001",
    A6: "CALENDAR_A6_001",
};

const EnhancedEcommerceCalendarNames: Dictionary<string> = {
    CALENDAR_A5_001: "Kalender A5",
    CALENDAR_A6_001: "Kalender A6",
};

const EnhancedEcommerceNotebookNames: Dictionary<string> = {
    NOTEBOOK_A5_001: "Notebook A5",
    NOTEBOOK_A6_001: "Notebook A6",
};

const DefaultSpiralA5: SpiralItem = {
    id: 3,
    articleNumber: "2003",
    text: "Silvertråd för A5",
    sortOrder: 3,
    hexColor: "#6C6D70",
    image: "spiral-#-silver.png",
    isMetalic: false,
};

const DefaultSpiralA6: SpiralItem = {
    id: 9,
    articleNumber: "2013",
    text: "Silvertråd för A6",
    sortOrder: 3,
    hexColor: "#6C6D70",
    image: "spiral-#-silver.png",
    isMetalic: false,
};

const DefaultSpiralB5: SpiralItem = {
    id: 3,
    articleNumber: "2003",
    text: "Silvertråd för A5",
    sortOrder: 3,
    hexColor: "#6C6D70",
    image: "spiral-#-silver.png",
    isMetalic: false,
};

const DefaultRubberband: RubberBandItem = {
    id: 2,
    articleNumber: "3002",
    text: "Vit A5",
    sortOrder: 1,
    hexColor: "#F5F5F5",
    image: "rubberband-repeat-white.png",
    isMetalic: false,
};

const DefaultCoverTextCalendarA5: TextInformation = {
    text: "2024",
    x: 750,
    y: 2000,
    fontSize: "144",
    color: "#FFFFFF",
    rotation: 0,
    fontId: 0,
    fontSetName: null,
};

const DefaultCoverTextCalendarA6: TextInformation = {
    text: "2024",
    x: 530,
    y: 1414,
    fontSize: "96",
    color: "#FFFFFF",
    rotation: 0,
    fontId: 0,
    fontSetName: null,
};

const DefaultCoverTextCalendarB5: TextInformation = {
    text: "2024",
    x: 910,
    y: 2400,
    fontSize: "180",
    color: "#FFFFFF",
    rotation: 0,
    fontId: 0,
    fontSetName: null,
};

const DefaultCoverTextNotebookA5 = {
    ...DefaultCoverTextCalendarA5,
    text: "My notebook",
};

const DefaultCoverTextNotebookA6 = {
    ...DefaultCoverTextCalendarA6,
    text: "My notebook",
};

const DefaultCoverTextNotebookB5 = {
    ...DefaultCoverTextCalendarB5,
    text: "My notebook",
};

const DefaultNumberOfSpirals: number = 24;

const DefaultSpiralsList: Array<{ calendarSize: string; value: SpiralItem }> = [
    {
        calendarSize: "A5",
        value: DefaultSpiralA5,
    },
    {
        calendarSize: "A6",
        value: DefaultSpiralA6,
    },
    {
        calendarSize: "B5",
        value: DefaultSpiralB5,
    },
];

const DefaultRubberbandsList: Array<{
    calendarSize: string;
    value: RubberBandItem;
}> = [
    {
        calendarSize: "A5",
        value: DefaultRubberband,
    },
    {
        calendarSize: "A6",
        value: {
            id: 12,
            articleNumber: "3022",
            text: "Vit A6",
            sortOrder: 1,
            hexColor: "#F5F5F5",
            image: "rubberband-repeat-white.png",
            isMetalic: false,
        },
    },
    {
        calendarSize: "B5",
        value: DefaultRubberband,
    },
];

const DefaultNumberOfSpiralsList: Array<{
    calendarSize: string;
    value: number;
}> = [
    {
        calendarSize: "A5",
        value: DefaultNumberOfSpirals,
    },
    {
        calendarSize: "A6",
        value: 17,
    },
    {
        calendarSize: "B5",
        value: 19,
    },
];

type checkoutLogoOptions = Dictionary<string>;

const paymentProviderLogos: checkoutLogoOptions = {
    default: "/images/payment-providers/default.png",
    klarna: "/images/payment-providers/klarna.png",
    paypal: "/images/payment-providers/paypal.png",
    swish: "/images/payment-providers/swish.png",
};

const shippingProviderLogos: checkoutLogoOptions = {
    default: "/images/shipping-providers/default.png",
    ups: "/images/shipping-providers/ups.png",
};

const MomentLocales: Dictionary<string> = {
    "sv-SE": "sv",
    "en-GB": "en",
    "de-DE": "de",
    "de-AT": "de-at",
    "en-IE": "en",
    "en-US": "en",
    "da-DK": "da",
    "fi-FI": "fi",
    "fr-FR": "fr",
    "nl-NL": "nl",
    "it-IT": "it",
    "es-ES": "es",
    "nb-NO": "nb",
    "de-CH": "de-ch",
};

const MomentTimeZones: Dictionary<string> = {
    "sv-SE": "Europe/Stockholm",
    "en-GB": "Europe/London",
    "de-DE": "Europe/Berlin",
    "de-AT": "Europe/Vienna",
    "en-IE": "Europe/Dublin",
    "en-US": "America/Chicago",
    "da-DK": "Europe/Copenhagen",
    "fi-FI": "Europe/Helsinki",
    "fr-FR": "Europe/Paris",
    "nl-NL": "Europe/Amsterdam",
    "it-IT": "Europe/Rome",
    "es-ES": "Europe/Madrid",
    "nb-NO": "Europe/Oslo",
    "de-CH": "Europe/Zurich",
};

const cultureCountries = [
    { culture: "sv-SE", country: "SE" },
    { culture: "en-GB", country: "GB" },
    { culture: "de-DE", country: "DE" },
    { culture: "de-AT", country: "AT" },
    { culture: "en-IE", country: "IE" },
    { culture: "en-US", country: "US" },
    { culture: "da-DK", country: "DK" },
    { culture: "fi-FI", country: "FI" },
    { culture: "fr-FR", country: "FR" },
    { culture: "nl-NL", country: "NL" },
    { culture: "it-IT", country: "IT" },
    { culture: "es-ES", country: "ES" },
    { culture: "nb-NO", country: "NO" },
    { culture: "de-CH", country: "CH" },
];

export {
    NavConfig,
    LightBoxTheme,
    NavigationConfig,
    NavigationOption,
    NavigationComponent,
    NavigationComponents,
    MainNavigationItem,
    NavigationValue,
    LOCAL_STORAGE_RELOAD,
    LOCAL_STORAGE_RELOAD_CALENDAR_A5,
    LOCAL_STORAGE_RELOAD_CALENDAR_A6,
    LOCAL_STORAGE_RELOAD_CALENDAR_B5,
    LOCAL_STORAGE_RELOAD_NOTEBOOK_A5,
    LOCAL_STORAGE_RELOAD_NOTEBOOK_A6,
    LOCAL_STORAGE_RELOAD_NOTEBOOK_B5,
    LOCAL_STORAGE_CHECKOUT,
    LOCAL_STORAGE_CONFIGURATION,
    LOCAL_STORAGE_UPLOADED_COVER_IMAGES,
    LOCAL_STORAGE_UPLOADED_OWNER_PAGE_IMAGES,
    COOKIE_USER_JWT,
    COOKIE_USER_EXPIRE_DAYS,
    MaxTemporaryCalendarStorageHours,
    LocalStorageReloadItem,
    LocalStorageTemporaryCalendar,
    LocalStorageCheckoutItem,
    removeEmojis,
    RecurringYear,
    DefaultSize,
    DefaultCulture,
    DefaultCurrency,
    DefaultType,
    NotebookArticleNames,
    CalendarArticleNames,
    EnhancedEcommerceCalendarNames,
    EnhancedEcommerceNotebookNames,
    CalendarSteps,
    DefaultSpiralA5,
    DefaultSpiralA6,
    DefaultRubberband,
    DefaultCoverTextCalendarA5,
    DefaultCoverTextCalendarA6,
    DefaultCoverTextCalendarB5,
    DefaultCoverTextNotebookA5,
    DefaultCoverTextNotebookA6,
    DefaultCoverTextNotebookB5,
    DefaultNumberOfSpirals,
    DefaultSpiralsList,
    DefaultRubberbandsList,
    DefaultNumberOfSpiralsList,
    SvgCalendarLayout,
    LayoutFont,
    LayoutPage,
    LayoutApiResponse,
    LayoutResource,
    LayoutEndpoints,
    ModuleResource,
    DayModuleResource,
    DayModuleElement,
    WeekModuleResource,
    ModuleOption,
    ModuleEndpoints,
    DayModuleOption,
    WeekModuleOption,
    WeekModuleElement,
    paymentProviderLogos,
    shippingProviderLogos,
    MomentLocales,
    MomentTimeZones,
    CalendarStepGuide,
    FastTrackStepGuide,
    NotebookStepGuide,
    cultureCountries,
};
